import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';

import { WeeklyChartComponent } from '../charts/weekly-chart.component';
import { ReportChromeService } from '../report-chrome.service';
import { ReportTPipe } from '../report-i18n.pipe';
import { ReportI18nService } from '../report-i18n.service';
import { ReportRevealDirective } from '../report-reveal.directive';
import { SubjectReportView } from '../report.model';

/** Racine des libellés de semaine dans le bundle du rapport. */
const WEEK_KEY = 'weekly.weeks.week';

/** Une pastille de légende : la clé de couleur d'une colonne du graphique. */
interface WeeklyLegendEntry {
  /** Code du référentiel, non localisé — l'identité métier de la compétence. */
  code: string;
  /** Code court affiché (`c1` / `s1` / `م1`). */
  shortCode: string;
  color: string;
}

/**
 * Section « plan de remédiation par semaine » d'une matière.
 *
 * Le graphique dit *quand*, la grille dit *quoi* : les deux lisent la même
 * liste, dans le même ordre, et la couleur d'une compétence est la même que
 * dans l'anneau. C'est cette constance qui permet de suivre une lacune d'un
 * bloc à l'autre sans relire son intitulé.
 *
 * Les pastilles sont colorées par liaison de style depuis le modèle. Le
 * document d'origine les repeignait après coup en parcourant le DOM à la
 * bascule de thème ; ici la couleur est une donnée de vue, et changer de thème
 * recalcule la vue.
 */
@Component({
  selector: 's2e-report-weekly',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportTPipe, ReportRevealDirective, WeeklyChartComponent],
  templateUrl: './subject-weekly.section.html',
  host: { '[style.display]': "'contents'" },
})
export class SubjectWeeklySection {
  private readonly i18n = inject(ReportI18nService);
  protected readonly chrome = inject(ReportChromeService);

  readonly subject = input.required<SubjectReportView>();
  /** Longueur du plan, décidée par le back : elle dépend du nombre de lacunes. */
  readonly planWeeks = input.required<number>();

  /**
   * Légende, bâtie sur les colonnes du graphique et non sur le plan.
   *
   * Les deux listes portent les mêmes compétences, mais pas forcément dans le
   * même ordre : les colonnes suivent l'ordre du référentiel tandis que le plan
   * les regroupe par semaine, et le back ordonne les lacunes selon leurs
   * prérequis. Lire la légende sur les barres est le seul moyen d'être sûr que
   * les deux ne divergeront jamais.
   *
   * Le code brut, lui, n'est pas dans les données du graphique : on le retrouve
   * par le code court, qui identifie la compétence à l'intérieur de sa matière.
   */
  protected readonly legend = computed<WeeklyLegendEntry[]>(() => {
    const byShortCode = new Map(
      this.subject().competencies.map((competency) => [competency.shortCode, competency.code]),
    );
    return this.subject().charts.weekly.map((bar) => ({
      code: byShortCode.get(bar.code) ?? bar.code,
      shortCode: bar.code,
      color: bar.color,
    }));
  });

  protected readonly codeDir = computed(() => this.i18n.codeDirection());

  protected readonly ariaLabel = computed(() =>
    this.i18n.t('weekly.aria', { count: this.subject().charts.weekly.length }),
  );
  protected readonly scheduledIn = computed(() => this.i18n.t('weekly.scheduledIn'));

  /** Libellés « Semaine n », index 0 = semaine 1. */
  protected readonly weekLabels = computed<string[]>(() => {
    const total = Math.max(0, this.planWeeks());
    const labels: string[] = [];
    for (let week = 1; week <= total; week += 1) labels.push(this.weekLabel(week));
    return labels;
  });

  /**
   * Libellé d'une semaine, avec repli au-delà de celles que porte le bundle.
   *
   * La maquette n'avait que quatre semaines, donc quatre clés ; la longueur du
   * plan est maintenant une donnée et peut les dépasser. Plutôt que d'inventer
   * des clés absentes, on décline le libellé de la semaine 1 en y remplaçant le
   * numéro : le mot reste celui de la langue courante, dans les trois langues.
   * Le moteur i18n rend la clé elle-même quand elle manque, ce qui sert ici de
   * détection.
   */
  private weekLabel(week: number): string {
    const key = `${WEEK_KEY}${week}`;
    const label = this.i18n.t(key);
    if (label !== key) return label;
    return this.i18n.t(`${WEEK_KEY}1`).replace(/\d+/, this.i18n.num(week));
  }
}
